import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { ArrowLeft, Loader2, MessageCircle, Trash2, Ban } from "lucide-react";
import { Header } from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";

export default function AdminComments() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  const refresh = async () => {
    try {
      const r = await api.get("/admin/comments?limit=200");
      setItems(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { refresh(); }, []);

  const hardDelete = async (id) => {
    if (!window.confirm("Permanently delete this comment? Cannot be undone.")) return;
    try {
      await api.delete(`/admin/comments/${id}`);
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const ban = async (userId, name) => {
    if (!window.confirm(`Ban ${name || "this user"}? They'll be logged out everywhere and unable to post.`)) return;
    try {
      await api.post(`/admin/commenters/${userId}/ban`);
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div
      className="admin-chrome min-h-screen bg-white text-[var(--brand-ink)]"
      data-testid="page-admin-comments"
    >
      <Header />
      <main className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14">
        <RouterLink
          to="/admin"
          className="inline-flex items-center gap-1.5 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
        >
          <ArrowLeft size={12} /> Back to admin
        </RouterLink>

        <div className="mt-6 mb-8">
          <div className="flex items-center gap-2 text-brand-accent">
            <MessageCircle size={16} strokeWidth={2.5} />
            <span className="label-kicker">Comments moderation</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Recent comments.
          </h1>
          <p className="mt-2 text-sm text-[var(--brand-body)]">
            Every comment posted on the site, newest first. Soft-deletes
            (by the author) are shown greyed out. Use{" "}
            <strong>Delete</strong> to hard-remove spam, or{" "}
            <strong>Ban</strong> to permanently block a user.
          </p>
        </div>

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="admin-comments-error"
          >
            {error}
          </p>
        ) : null}

        {items === null ? (
          <div className="flex items-center gap-2 text-sm text-[var(--brand-body)]">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <p className="rounded-md border border-dashed border-black/15 p-8 text-center text-sm text-[var(--brand-body)]">
            No comments yet.
          </p>
        ) : (
          <ul className="space-y-3" data-testid="admin-comments-list">
            {items.map((c) => {
              const body = c.deleted ? c.raw_body : c.body;
              return (
                <li
                  key={c.id}
                  className={`rounded-md border p-4 ${c.deleted ? "border-black/10 bg-black/5" : "border-black/10 bg-white"}`}
                  data-testid={`admin-comment-${c.id}`}
                >
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-display text-sm font-bold">
                      {c.author?.name || c.author_email || "?"}
                    </span>
                    <span className="label-kicker text-[var(--brand-body)]">
                      {c.author_email}
                    </span>
                    {c.deleted ? (
                      <span className="label-kicker rounded bg-black/10 px-1.5 py-0.5">
                        Soft-deleted
                      </span>
                    ) : null}
                    {c.parent_id ? (
                      <span className="label-kicker rounded bg-[#6CABDD]/15 px-1.5 py-0.5">
                        Reply
                      </span>
                    ) : null}
                    <span className="ml-auto label-kicker text-[var(--brand-body)]">
                      {c.created_at?.slice(0, 16).replace("T", " ")}
                    </span>
                  </div>
                  <p className={`mt-2 whitespace-pre-wrap text-sm ${c.deleted ? "italic text-[var(--brand-body)]" : "text-[var(--brand-ink)]"}`}>
                    {body || "[empty]"}
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <a
                      href={`/post/${c.post_id}#comment-${c.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="label-kicker text-[var(--brand-body)] underline underline-offset-4 hover:text-[var(--brand-ink)]"
                    >
                      Open post
                    </a>
                    <span className="opacity-30">·</span>
                    <button
                      type="button"
                      onClick={() => hardDelete(c.id)}
                      data-testid={`admin-comment-${c.id}-delete`}
                      className="inline-flex items-center gap-1 label-kicker text-[#E63946] hover:opacity-80"
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                    {c.author?.user_id ? (
                      <button
                        type="button"
                        onClick={() => ban(c.author.user_id, c.author.name)}
                        data-testid={`admin-comment-${c.id}-ban`}
                        className="inline-flex items-center gap-1 label-kicker text-[#E63946] hover:opacity-80"
                      >
                        <Ban size={11} /> Ban user
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}
